/**
 * Dates, times, numbers and relative times in the rendered app language.
 * Every user-visible date goes through here instead of a hard-coded locale,
 * so German shows "17. Okt." and Chinese "10月17日".
 */
import { activeLocaleTag } from "./runtime";

type DateInput = Date | string | number;

/** `Intl.ListFormat` (ES2021; the project's TypeScript lib is ES2020). */
interface ListFormatOptions {
  type: "conjunction" | "disjunction" | "unit";
  style: "long" | "short" | "narrow";
}
interface ListFormatter {
  format(items: string[]): string;
}
type ListFormatConstructor = new (
  locale: string,
  options: ListFormatOptions,
) => ListFormatter;

type Formatter =
  | Intl.DateTimeFormat
  | Intl.NumberFormat
  | Intl.RelativeTimeFormat
  | ListFormatter;

const formatterCache = new Map<string, Formatter>();

function cached<F extends Formatter>(
  kind: string,
  options: object | undefined,
  create: (locale: string) => F,
): F {
  const locale = activeLocaleTag();
  const key = `${kind}|${locale}|${JSON.stringify(options ?? {})}`;
  let formatter = formatterCache.get(key) as F | undefined;
  if (!formatter) {
    formatter = create(locale);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/** A date, by default like "Oct 17, 2026" / "17. Okt. 2026" / "2026年10月17日". */
export function formatDate(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
): string {
  return cached(
    "date",
    options,
    (locale) => new Intl.DateTimeFormat(locale, options),
  ).format(toDate(value));
}

/** A time of day, by default hours and minutes in the language's clock. */
export function formatTime(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" },
): string {
  return formatDate(value, options);
}

export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return cached(
    "number",
    options,
    (locale) => new Intl.NumberFormat(locale, options),
  ).format(value);
}

const RELATIVE_STEPS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 7],
  ["week", 4.345],
  ["month", 12],
  ["year", Number.POSITIVE_INFINITY],
];

/**
 * How long ago (or until) `value` is, in the largest whole unit: "5 min ago",
 * "vor 5 Min.", "5分钟前". Under 30 seconds reads "now". `style: "narrow"`
 * gives the compact form lists use.
 */
export function formatRelativeTime(
  value: DateInput,
  {
    style = "short",
    now = Date.now(),
  }: { style?: Intl.RelativeTimeFormatStyle; now?: number } = {},
): string {
  const options: Intl.RelativeTimeFormatOptions = { numeric: "auto", style };
  const formatter = cached(
    "relative",
    options,
    (locale) => new Intl.RelativeTimeFormat(locale, options),
  );
  let amount = (toDate(value).getTime() - now) / 1000;
  if (Math.abs(amount) < 30) return formatter.format(0, "second");
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(amount) < size)
      return formatter.format(Math.trunc(amount), unit);
    amount /= size;
  }
  return formatter.format(Math.trunc(amount), "year");
}

/**
 * Items as one phrase in the app language: by default "a, b, and c" /
 * "a, b und c" / "a、b和c".
 */
export function formatList(
  items: string[],
  options: ListFormatOptions = { type: "conjunction", style: "long" },
): string {
  const ListFormat = (Intl as unknown as { ListFormat: ListFormatConstructor })
    .ListFormat;
  return cached(
    "list",
    options,
    (locale) => new ListFormat(locale, options),
  ).format(items);
}
